//* Libraries imports
import { motion } from "framer-motion";
import { ReactNode } from "react";
import { 
  FaHourglassHalf, 
  FaInfoCircle, 
  FaCheckCircle, 
  FaExclamationTriangle,
  FaClock,
  FaLock
} from "react-icons/fa";

//* Component imports
import { Icon } from "../../../components/common/Icon";

//* Styles imports
import styles from "./process-status.module.scss";

export type ProcessStatusType = 
  | "pending" 
  | "finished" 
  | "in-progress" 
  | "blocked" 
  | "warning" 
  | "locked";

interface ProcessStatusProps {
  type: ProcessStatusType;
  title: string;
  subtitle?: string;
  message: string;
  actionLabel?: string;
  onAction?: () => void;
  icon?: ReactNode;
  showBadge?: boolean;
  badgeText?: string;
  className?: string;
}

export function ProcessStatus({
  type,
  title,
  subtitle,
  message,
  actionLabel = "Voltar",
  onAction,
  icon,
  showBadge = true,
  badgeText,
  className = ""
}: ProcessStatusProps) {
    // Get default icon based on type if none provided
  const getDefaultIcon = () => {
    switch (type) {
      case "pending":
      case "in-progress":
        return <Icon icon={FaHourglassHalf} className={styles.mainIcon} />;
      case "finished":
        return <Icon icon={FaCheckCircle} className={styles.mainIcon} />;
      case "warning":
        return <Icon icon={FaExclamationTriangle} className={styles.mainIcon} />;
      case "blocked":
        return <Icon icon={FaClock} className={styles.mainIcon} />;
      case "locked":
        return <Icon icon={FaLock} className={styles.mainIcon} />;
      default:
        return <Icon icon={FaInfoCircle} className={styles.mainIcon} />;
    }
  };

  // Get default badge text based on type
  const getDefaultBadgeText = () => {
    switch (type) {
      case "pending":
        return "Pendente";
      case "in-progress":
        return "Em andamento";
      case "finished":
        return "Finalizado";
      case "warning":
        return "Atenção";
      case "blocked":
        return "Bloqueado";
      case "locked":
        return "Bloqueado";
      default:
        return "Status";
    }
  };  
  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5
      }
    }
  };

  const iconVariants = {
    hidden: { scale: 0, rotate: -180 },
    visible: {
      scale: 1,
      rotate: 0,
      transition: {
        delay: 0.3,
        duration: 0.5
      }
    }
  };

  const textVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        delay: 0.5,
        duration: 0.4
      }
    }
  };

  const buttonVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        delay: 0.7,
        duration: 0.3
      }
    },
    hover: {
      scale: 1.05,
      transition: { duration: 0.2 }
    },
    tap: {
      scale: 0.95
    }
  };

  return (
    <motion.div
      className={`${styles.container} ${styles[type]} ${className}`}
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      <motion.div className={styles.card} layout>
        <motion.div
          className={styles.iconContainer}
          variants={iconVariants}
        >
          {icon || getDefaultIcon()}
        </motion.div>        <motion.div className={styles.content} variants={textVariants}>
          <h2 className={styles.title}>
            <Icon icon={FaInfoCircle} className={styles.infoIcon} />
            {title}
          </h2>
          
          {subtitle && (
            <p className={styles.subtitle}>
              <strong>{subtitle}</strong>
            </p>
          )}

          <div className={styles.messageContainer}>
            <p className={styles.message}>
              {message}
            </p>
            
            {showBadge && (
              <div className={styles.statusInfo}>
                <span className={`${styles.statusBadge} ${styles[`badge-${type}`]}`}>
                  {badgeText || getDefaultBadgeText()}
                </span>
              </div>
            )}
          </div>

          {onAction && (
            <motion.button
              className={`${styles.actionButton} ${styles[`button-${type}`]}`}
              onClick={onAction}
              variants={buttonVariants}
              whileHover="hover"
              whileTap="tap"
            >
              {actionLabel}
            </motion.button>
          )}
        </motion.div>
      </motion.div>
    </motion.div>
  );
}
